import { Select, SelectContent, SelectItem, SelectTrigger, type selectTriggerVariants } from '@/components/ui/select';
import { languagePicker, locales } from '@/constants/languages';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type { VariantProps } from 'tailwind-variants';

type Props = {
  size?: VariantProps<typeof selectTriggerVariants>['size'];
};

export function LanguagePicker({ size }: Props) {
  const locale = useLocale();
  const router = useRouter();

  const getCurrentPath = (targetLocale: string) => {
    const { pathname, query } = router;

    // if the path is lp/somepath, redirect to the home page
    if (pathname.startsWith('/lp/')) {
      return { pathname: '/', query: {}, locale: targetLocale };
    }

    return { pathname, query, locale: targetLocale };
  };

  const handleValueChange = (value: string) => {
    if (value !== locale) {
      const { pathname, query, locale: targetLocale } = getCurrentPath(value);
      router.push({ pathname, query }, undefined, { locale: targetLocale });
    }
  };

  return (
    <Select defaultValue={locale} onValueChange={handleValueChange}>
      <SelectTrigger size={size} shadow={false}>
        <div className="flex items-center justify-between gap-2">
          <span>{locale.toUpperCase()}</span>
          <Image
            src={`/assets/images/flags/${locale.toUpperCase()}.png`}
            className="hidden md:block"
            alt={locale}
            width={16}
            height={12}
          />
        </div>
      </SelectTrigger>
      <SelectContent align="center" className="min-w-44">
        {locales.map((codeLocale) => (
          <SelectItem key={codeLocale} value={codeLocale}>
            <div className="flex items-center gap-2">
              <Image
                src={`/assets/images/flags/${codeLocale.toUpperCase()}.png`}
                alt={codeLocale}
                width={16}
                height={12}
              />
              <span>{languagePicker[codeLocale]}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
